import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { BarChart, PieChart, Pie, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Users, BookOpen, UserCheck, ChevronRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const { api, user } = useAuth();
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0); // Add state for total students
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation('dashboard');

  const navigate = useNavigate()
  
  // Get current date for attendance range
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);
  
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel using api client
        const [studentsRes, teachersRes, parentsRes, classesRes, attendanceRes] = await Promise.all([
          api.get(`/api/v1/admin/schools/${user.school_reg}/students`),
          api.get(`/api/v1/teachers/schools/${user.school_reg}/teachers`),
          api.get(`/api/v1/admin/schools/${user.school_reg}/parents`),
          api.get(`/api/v1/admin/schools/${user.school_reg}/classes`),
          api.get(`/api/v1/attendance/schools/${user.school_id}/daily-records?start_date=${formatDate(lastWeek)}&end_date=${formatDate(today)}`)
        ]);
        
        // API client already returns parsed JSON data
        setStudents(studentsRes.data.items || []);
        setTotalStudents(studentsRes.data.total || 0); // Set the total student count
        setTeachers(teachersRes.data || []);
        setParents(parentsRes.data.data || []);
        setClasses(classesRes.data || []);
        setAttendance(attendanceRes.data['students-attendance'] || []);
        
      } catch (err) {
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user.school_reg, api]);
  
  // Calculate total streams
  const totalStreams = classes.reduce((total, classItem) => total + (classItem.streams?.length || 0), 0);
  
  // Process attendance data for charts
  const processAttendanceData = () => {
    const statusCounts = { present: 0, absent: 0, late: 0 };
    const dailyAttendance = [];
    
    attendance.forEach(day => {
      const date = day.date;
      const dailyCounts = { date, present: 0, absent: 0, late: 0, total: day.records.length };
      
      day.records.forEach(record => {
        statusCounts[record.status]++;
        dailyCounts[record.status]++;
      });
      
      dailyAttendance.push(dailyCounts);
    });
    
    return { statusCounts, dailyAttendance };
  };
  
  const { statusCounts, dailyAttendance } = processAttendanceData();
  
  // Prepare data for pie chart
  const pieChartData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  
  // Colors for different status types
  const statusColors = {
    present: '#4CAF50',
    absent: '#F44336',
    late: '#FFC107'
  };
  
  // Calculate gender distribution
  const genderDistribution = students.reduce(
    (acc, student) => {
      if (student.gender === 'Male') acc.male++;
      else if (student.gender === 'Female') acc.female++;
      return acc;
    }, 
    { male: 0, female: 0 }
  );
  
  const genderChartData = [
    { name: 'Male', value: genderDistribution.male },
    { name: 'Female', value: genderDistribution.female }
  ];
  
  const genderColors = ['#2196F3', '#E91E63'];

  const handleViewAllAttendance = () => {
    navigate('/attendance-report');
  };

  const handleAddteacher = () => {
    navigate('/register-teacher');
  };

  const handleAddStudent = () => {
    navigate('/register-student');
  };

  const takeAttendance = () => {
    navigate('/mark-attendance');
  };

  const handleManageSessions = () => {
    navigate('/session-management');
  };

  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg font-medium text-red-500">{error}</div>
      </div>
    );
  }
  
  // Check if RTL is enabled
  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';
  
  return (
    <div className={`bg-gray-50 min-h-screen ${rtlClass}`}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">{t('title')}</h1>
        <p className="text-gray-500">{t('welcome', { name: user.name || 'Administrator' })}</p>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          icon={<Users size={24} />} 
          title={t('stats.totalStudents')} 
          value={totalStudents}
          color="bg-blue-500"
        />
        <StatCard 
          icon={<UserCheck size={24} />} 
          title={t('stats.totalTeachers')} 
          value={teachers.length} 
          color="bg-green-500"
        />
        <StatCard 
          icon={<Users size={24} />} 
          title={t('stats.totalParents')} 
          value={parents.length} 
          color="bg-purple-500"
        />
        <StatCard 
          icon={<BookOpen size={24} />} 
          title={t('stats.classesStreams')} 
          value={`${classes.length} / ${totalStreams}`} 
          color="bg-amber-500"
        />
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Attendance Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t('charts.weeklyAttendance')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyAttendance} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="present" stackId="a" fill={statusColors.present} name={t('attendance.present')} />
                <Bar dataKey="late" stackId="a" fill={statusColors.late} name={t('attendance.late')} />
                <Bar dataKey="absent" stackId="a" fill={statusColors.absent} name={t('attendance.absent')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Attendance Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t('charts.attendanceDistribution')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({name, percent}) => `${t(`attendance.${name}`)} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={statusColors[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col justify-center">
              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: statusColors.present }}></div>
                  <span className="text-sm">{t('attendance.present')}</span>
                </div>
                <div className="text-2xl font-bold">{statusCounts.present}</div>
              </div>
              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: statusColors.late }}></div>
                  <span className="text-sm">{t('attendance.late')}</span>
                </div>
                <div className="text-2xl font-bold">{statusCounts.late}</div>
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <div className="w-4 h-4 rounded mr-2" style={{ backgroundColor: statusColors.absent }}></div>
                  <span className="text-sm">{t('attendance.absent')}</span>
                </div>
                <div className="text-2xl font-bold">{statusCounts.absent}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student Gender Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t('charts.genderDistribution')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={genderChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {genderChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={genderColors[index % genderColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Classes Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">{t('charts.classesOverview')}</h2>
          <div className="overflow-y-auto max-h-64">
            {classes.map(classItem => (
              <div key={classItem.id} className="mb-4 last:mb-0">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">{classItem.name}</h3>
                  <span className="text-sm text-gray-500">{classItem.streams?.length || 0} {t('classes.streams')}</span>
                </div>
                <div className="space-y-2">
                  {classItem.streams?.map(stream => (
                    <div key={stream.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span>{stream.name}</span>
                      <span className="text-sm text-gray-500">
                        {students.filter(s => s.stream_id === stream.id).length} {t('classes.students')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Recent Attendance */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t('charts.recentAttendance')}</h2>
            <button className="text-blue-500 text-sm flex items-center" onClick={handleViewAllAttendance}>
              {t('attendance.viewAll')} <ChevronRight size={16} />
            </button>
          </div>
          <div className="overflow-y-auto max-h-64">
            {attendance.length > 0 && attendance[0].records.slice(0, 5).map(record => (
              <div key={record.studentId} className="flex items-center p-2 border-b last:border-b-0">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  record.status === 'present' ? 'bg-green-500' : 
                  record.status === 'late' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <div className="flex-1">
                  <div className="font-medium">{record.studentName}</div>
                  <div className="text-xs text-gray-500">
                    {t('attendance.admNo')}: {record.admissionNumber} • {record.sessionName} {t('attendance.session')}
                  </div>
                </div>
                <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                  record.status === 'present' ? 'bg-green-100 text-green-800' : 
                  record.status === 'late' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                }`}>
                  {t(`attendance.${record.status}`)}
                </div>
              </div>
            ))}
            {(!attendance.length || !attendance[0].records.length) && (
              <div className="text-center py-4 text-gray-500">{t('attendance.noRecords')}</div>
            )}
          </div>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">{t('quickActions.title')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ActionCard title={t('quickActions.addTeacher')} icon={<Users size={20} />} onClick={handleAddteacher}/>
          <ActionCard title={t('quickActions.addStudent')} icon={<Users size={20} />} onClick={handleAddStudent}/>
          <ActionCard title={t('quickActions.takeAttendance')} icon={<UserCheck size={20} />} onClick={takeAttendance}/>
          <ActionCard title={t('quickActions.manageSessions')} icon={<Clock size={20} />} onClick={handleManageSessions}/>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, color }) => {
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4">
        <div className="flex items-center">
          <div className={`${color} rounded-full p-3 text-white mr-4`}>
            {icon}
          </div>
          <div>
            <h3 className="text-gray-500 text-sm">{title}</h3>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
      </div>
      <div className={`${color} h-1`}></div>
    </div>
  );
};

// Action Card Component
const ActionCard = ({ title, icon, onClick }) => {
  return (
    <button 
      className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center hover:bg-blue-50 transition-colors"
      onClick={onClick}
    >
      <div className="bg-blue-100 text-blue-600 rounded-full p-3 mb-2">
        {icon}
      </div>
      <span className="text-sm font-medium">{title}</span>
    </button>
  );
};

export default Dashboard;